import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAppState } from '../components/StateProvider';
import { useRecipeMemory } from '../components/RecipeProvider';

export default function Cooking() {
  const { mealId = '' } = useParams();
  const navigate = useNavigate();
  const { state, loading, busy, startCooking, finishCooking } = useAppState();
  const { saveMeal, isMealSaved, recipeBusy } = useRecipeMemory();
  const started = useRef(false);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const meal = state.meals.find((m) => m.id === mealId);

  useEffect(() => {
    if (loading || !meal || started.current || meal.status === 'cooked') return;
    if (meal.status === 'cooking' && meal.cookingSteps?.length) return;
    started.current = true;
    void startCooking(meal.id);
  }, [loading, meal?.id, meal?.status, meal?.cookingSteps?.length]);

  if (loading) return <div className="page"><section className="card"><span className="eyebrow">Cooking mode</span><h2>Loading dinner…</h2></section></div>;

  if (!meal) return <div className="page"><section className="card empty-state"><span className="eyebrow">Cooking mode</span><h2>Meal not found.</h2><p>This meal may have been replaced by a newer weekly plan.</p><button className="primary" onClick={() => navigate('/')}>Back to Today</button></section></div>;

  const steps = meal.cookingSteps ?? [];
  const completed = meal.status === 'cooked';
  const currentMealId = meal.id;
  const saved = isMealSaved(currentMealId);

  function toggleStep(index: number) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  }

  async function finish() {
    const ok = await finishCooking(currentMealId);
    if (ok) navigate('/');
  }

  return <div className="page cooking-page">
    <div className="cooking-topline">
      <button className="ghost" onClick={() => navigate('/')}><span aria-hidden="true">←</span> Back to Today</button>
      <span className={`cooking-status ${completed ? 'done' : ''}`}>{completed ? 'Cooked' : meal.status === 'cooking' ? 'Cooking now' : 'Ready to cook'}</span>
    </div>

    <section className="card cooking-hero">
      <span className="eyebrow">{meal.day} dinner</span>
      <div className="section-title">
        <div><h1>{meal.name}</h1><p className="feature-reason">{meal.reason}</p></div>
        <span className="time-pill"><Icon name="clock" /> {meal.minutes} min</span>
      </div>
      <div className="cooking-meta"><span>{meal.servings} household servings</span><span>{meal.ingredients.length} ingredients</span></div>
    </section>

    <div className="two-col cooking-grid">
      <section className="card">
        <div className="section-title"><div><span className="eyebrow">Get ready</span><h2>Ingredients</h2></div></div>
        <div className="cooking-ingredients">
          {meal.ingredients.map((ingredient) => <div key={`${ingredient.name}-${ingredient.unit}`}><span>{ingredient.name}</span><strong>{ingredient.quantity} {ingredient.unit}</strong></div>)}
        </div>
      </section>

      <section className="card">
        <div className="section-title"><div><span className="eyebrow">Cooking guide</span><h2>Step by step</h2></div></div>
        {!steps.length ? <div className="cooking-loading"><span className="cooking-pulse" /><div><strong>{busy ? 'Weekstead is preparing the instructions…' : 'Preparing instructions…'}</strong><small>The guide is generated from this exact meal and its ingredients.</small></div></div> : <ol className="cooking-steps">
          {steps.map((step, index) => <li key={`${index}-${step.slice(0, 24)}`} className={checked.has(index) ? 'checked' : ''} onClick={() => toggleStep(index)}>
            <button type="button" className="step-check" aria-label={`Mark step ${index + 1} complete`}>{checked.has(index) ? '✓' : index + 1}</button>
            <span>{step}</span>
          </li>)}
        </ol>}
      </section>
    </div>

    <section className="card cooking-finish">
      <div><span className="eyebrow">Dinner state</span><h2>{completed ? 'Dinner is complete.' : 'Finished cooking?'}</h2><p>{completed ? 'Weekstead has recorded this meal as cooked.' : 'Save the version your household likes, then Weekstead can recognize when your kitchen has the ingredients to make it again.'}</p></div>
      <div className="cooking-finish-actions"><button className={`outline save-recipe-button ${saved ? 'saved' : ''}`} disabled={recipeBusy || !steps.length} onClick={() => void saveMeal(currentMealId, meal.name)}><Icon name="recipes" /> {saved ? 'Update saved recipe' : 'Save recipe'}</button>{!completed ? <button className="primary" disabled={busy || !steps.length} onClick={() => void finish()}>{busy ? 'Updating…' : 'Finish cooking'}</button> : <button className="primary" onClick={() => navigate('/')}>Return to Today</button>}</div>
    </section>
  </div>;
}